import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import api from '../services/api';

const ExerciseAutocomplete = ({ onSelect, placeholder = "Buscar ejercicio...", selectedExercise = null }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceTimer = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search exercises with debounce
  useEffect(() => {
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Don't search if query is less than 3 characters
    if (query.length < 3) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    // Set loading state
    setLoading(true);

    // Debounce search by 300ms
    debounceTimer.current = setTimeout(async () => {
      try {
        const response = await api.get(`/exercises/search?q=${encodeURIComponent(query)}`);
        setResults(response.data);
        setShowDropdown(true);
        setHighlightedIndex(-1);
      } catch (error) {
        console.error('Error searching exercises:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    // Cleanup
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [query]);

  const handleSelect = (exercise) => {
    setQuery('');
    setResults([]);
    setShowDropdown(false);
    onSelect(exercise);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
          handleSelect(results[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      {/* Selected Exercise Display */}
      {selectedExercise && (
        <div className="mb-3 p-3 bg-indigo-50 border-2 border-indigo-200 rounded-lg flex items-center justify-between">
          <div>
            <p className="font-bold text-indigo-900">{selectedExercise.name}</p>
            <p className="text-xs text-indigo-600">
              {selectedExercise.muscle_group?.name}
              {selectedExercise.is_global && ' • 🌐 Global'}
            </p>
          </div>
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search size={20} className="text-gray-400" />
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-medium"
        />
        {loading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </div>

      {/* Search hint */}
      {query.length > 0 && query.length < 3 && (
        <p className="text-xs text-gray-500 mt-1 ml-1">
          Escribe al menos 3 caracteres para buscar...
        </p>
      )}

      {/* Dropdown Results */}
      {showDropdown && results.length > 0 && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-2xl border-2 border-indigo-200 max-h-80 overflow-y-auto">
          {results.map((exercise, index) => (
            <div
              key={exercise.id}
              onClick={() => handleSelect(exercise)}
              className={`p-3 cursor-pointer border-b border-gray-100 hover:bg-indigo-50 transition-colors ${
                index === highlightedIndex ? 'bg-indigo-100' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-bold text-gray-900">{exercise.name}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full font-semibold">
                      {exercise.muscle_group?.name}
                    </span>
                    {exercise.is_global && (
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-semibold flex items-center gap-1">
                        🌐 Global
                      </span>
                    )}
                  </div>
                  {exercise.description && (
                    <p className="text-xs text-gray-600 mt-1 line-clamp-1">
                      {exercise.description}
                    </p>
                  )}
                </div>
                <span className="text-2xl ml-3">💪</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results message */}
      {showDropdown && !loading && query.length >= 3 && results.length === 0 && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-2xl border-2 border-gray-200 p-6 text-center">
          <p className="text-gray-600 font-medium">😕 No se encontraron ejercicios</p>
          <p className="text-xs text-gray-500 mt-1">Intenta con otro término de búsqueda</p>
        </div>
      )}
    </div>
  );
};

export default ExerciseAutocomplete;
